import React, { useState, useEffect, useMemo } from "react";
import Collapse from "@mui/material/Collapse";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import GTranslateIcon from "@mui/icons-material/GTranslate";
import axios from "axios";
import SecureLS from "secure-ls";

import { getAuthCredentials } from "../../../utils/auth";
import { translateText } from "../../../component/TranslateText";
import AddMenuCategoryDialog from "./AddMenuCategoryDialog";
import SubRecipeDialog from "./SubRecipeDialog";
import HandleCropMenuImage from "../Functions/HandeCropMenuImage";

const ls = new SecureLS({ encodingType: "aes" });

/* ----------------------- ID utils (robust) ----------------------- */
const HEX24 = /^[a-fA-F0-9]{24}$/;
const isValidHexId = (s) => typeof s === "string" && HEX24.test(s);

const normalizeId = (id) => {
  if (!id) return "";
  // already a string id
  if (typeof id === "string") {
    // guard against "[object Object]"
    if (id === "[object Object]") return "";
    return id;
  }
  // populated or document: {_id: ...}
  if (typeof id === "object") {
    if (id.$oid) return String(id.$oid);
    if (id._id) return normalizeId(id._id);
    if (typeof id.toHexString === "function") return id.toHexString();
    if (typeof id.toString === "function") {
      const s = id.toString();
      return isValidHexId(s) ? s : "";
    }
    return "";
  }
  // numbers etc.
  try {
    const s = String(id);
    return isValidHexId(s) ? s : "";
  } catch {
    return "";
  }
};

const safeRevoke = (url) => {
  try {
    if (url) URL.revokeObjectURL(url);
  } catch {}
};

const qtyKeyOf = (row) => row.uiKey || row.inventoryId;
/* ---------------------------------------------------------------- */

const CreateMenuItemDialog = ({
  open,
  onClose,
  onSave,
  ingredients,
  menuCategories,
  setMenuCategories,
  menuItems,
  fetchMenuCategories,
}) => {
  const [name, setName] = useState("");
  const [translations, setTranslations] = useState({
    en: "",
    tl: "",
    zh: "",
    th: "",
  });
  const [showTranslations, setShowTranslations] = useState(false);
  const [loadingTranslation, setLoadingTranslation] = useState({
    en: false,
    tl: false,
    zh: false,
    th: false,
  });

  const [tags, setTags] = useState("");
  const [category, setCategory] = useState("");
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState("");
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [ingredientQuantities, setIngredientQuantities] = useState({});
  const [price, setPrice] = useState("");
  const [code, setCode] = useState("");
  const [uniqueName, setUniqueName] = useState("");
  const [description, setDescription] = useState("");
  const [addons, setAddons] = useState([]);
  const [processTime, setProcessTime] = useState(0);
  const [nameError, setNameError] = useState("");
  const [menuPax, setMenuPax] = useState(1);

  const [subRecipeOpen, setSubRecipeOpen] = useState(false);
  const [showSubRecipes, setShowSubRecipes] = useState(false);

  const { token } = getAuthCredentials();

  // Quick maps for lookups
  const inventoryById = useMemo(
    () => new Map((ingredients || []).map((i) => [normalizeId(i._id), i])),
    [ingredients]
  );
  const menuById = useMemo(
    () => new Map((menuItems || []).map((m) => [normalizeId(m._id), m])),
    [menuItems]
  );

  const handleTranslate = async (lang) => {
    setLoadingTranslation((prev) => ({ ...prev, [lang]: true }));
    try {
      const translated = await translateText(name, lang);
      setTranslations((prev) => ({ ...prev, [lang]: translated }));
    } catch (error) {
      console.error(`Error translating to ${lang}:`, error);
    } finally {
      setLoadingTranslation((prev) => ({ ...prev, [lang]: false }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024;
    if (!file) return;
    if (!allowed.includes(file.type)) {
      setImageError(
        "Unsupported file type. Only JPEG, PNG, and GIF are allowed."
      );
      return;
    }
    if (file.size > maxSize) {
      setImageError("File size exceeds 5 MB.");
      return;
    }
    setImageError("");
    safeRevoke(cropSrc);
    setCropSrc(URL.createObjectURL(file));
    setCropperOpen(true);
  };

  const handleCropSave = (blob) => {
    const previewUrl = URL.createObjectURL(blob);
    setImage(previewUrl);
    setImageFile(new File([blob], "menu-image.jpg", { type: blob.type }));
    setCropperOpen(false);
    safeRevoke(cropSrc);
    setCropSrc(null);
  };
  const handleCropCancel = () => {
    setCropperOpen(false);
    safeRevoke(cropSrc);
    setCropSrc(null);
  };

  const handleDeleteCategory = async (categoryId) => {
    const user = ls.get("user");
    const posId = user?.posId;
    if (!token || !posId) {
      onClose();
      return;
    }
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/delete-category`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { categoryId, posId },
        }
      );
      await fetchMenuCategories();
      alert(`Category has been successfully deleted.`);
    } catch (error) {
      console.error("Error deleting category:", error);
      alert("Failed to delete category. Please try again.");
    }
  };

  const handleAddIngredient = (ingredient) => {
    const invId = normalizeId(ingredient._id);
    if (!invId) return;
    if (
      !selectedIngredients.some(
        (item) => normalizeId(item.inventoryId) === invId
      )
    ) {
      const uiKey = `ing:${invId}`;
      setSelectedIngredients([
        ...selectedIngredients,
        { ...ingredient, uiKey, inventoryId: invId },
      ]);
      setIngredientQuantities((prev) => ({ ...prev, [uiKey]: 1 }));
    }
  };

  const handleRemoveIngredient = (ingredient) => {
    // remove sub-recipe + its children
    if (ingredient.__isSubRecipe) {
      const parentKey = qtyKeyOf(ingredient);
      const nextSel = selectedIngredients.filter(
        (it) => qtyKeyOf(it) !== parentKey && it.parentUIKey !== parentKey
      );
      const nextQty = { ...ingredientQuantities };
      delete nextQty[parentKey];
      selectedIngredients.forEach((it) => {
        if (it.parentUIKey === parentKey) delete nextQty[qtyKeyOf(it)];
      });
      setSelectedIngredients(nextSel);
      setIngredientQuantities(nextQty);
      return;
    }
    // regular
    const qKey = qtyKeyOf(ingredient);
    setSelectedIngredients(
      selectedIngredients.filter((i) => qtyKeyOf(i) !== qKey)
    );
    setIngredientQuantities((prev) => {
      const copy = { ...prev };
      delete copy[qKey];
      return copy;
    });
  };

  const handleQuantityChange = (keyOrId, value) => {
    const parsed = parseFloat(value);
    setIngredientQuantities((prev) => ({
      ...prev,
      [keyOrId]: isNaN(parsed) ? 1 : parsed,
    }));
  };

  /* Expand sub-recipe into real inventory rows (recursively) */
  const expandSubRecipeIngredients = (
    menuItem,
    parentUIKey,
    qtyMultiplier = 1,
    seen = new Set()
  ) => {
    const out = [];
    const mid = normalizeId(menuItem._id);
    if (!mid || seen.has(mid)) return out;
    seen.add(mid);

    const node = menuById.get(mid) || menuItem;
    const items = Array.isArray(node.menuIngredients)
      ? node.menuIngredients
      : [];

    items.forEach((mi, idx) => {
      // nested sub-recipe
      if (mi.subRecipeId) {
        const childId = normalizeId(mi.subRecipeId);
        const child = menuById.get(childId);
        const amt = Number(mi.amount || 1) * qtyMultiplier;
        if (child) {
          out.push(
            ...expandSubRecipeIngredients(
              child,
              parentUIKey,
              amt,
              new Set(seen)
            )
          );
        } else {
          console.warn("Sub-recipe not found in menuItems:", childId);
        }
        return;
      }

      // leaf: inventory
      const invId = normalizeId(mi.ingredientId);
      if (!invId) {
        console.warn(
          "Bad ingredientId in sub-recipe; skipping row:",
          mi.ingredientId
        );
        return;
      }

      const inv = inventoryById.get(invId);
      const base = Number(mi.amount || 1) * qtyMultiplier;
      const uiKey = `${parentUIKey}:ing:${invId}:${idx}`;

      if (inv) {
        out.push({
          ...inv,
          uiKey,
          inventoryId: invId,
          inventoryName: inv.inventoryName,
          unitOfMeasurement: inv.unitOfMeasurement,
          instances: inv.instances,
          parentUIKey,
          fromSubRecipeId: mid,
          fromSubRecipeName: node.menuName,
          baseAmountFromSub: base,
        });
      } else {
        // fallback minimal (still carries valid inventoryId)
        out.push({
          uiKey,
          inventoryId: invId,
          inventoryName: mi.ingredientName || "Unknown Ingredient",
          unitOfMeasurement: "",
          instances: [],
          parentUIKey,
          fromSubRecipeId: mid,
          fromSubRecipeName: node.menuName,
          baseAmountFromSub: base,
        });
      }
    });

    return out;
  };

  const handleAddSubRecipeItems = (pickedMenuItems) => {
    const nextSelected = [...selectedIngredients];
    const nextQty = { ...ingredientQuantities };

    pickedMenuItems.forEach((m) => {
      const srId = normalizeId(m._id);
      if (!srId) return;

      const already = nextSelected.some(
        (it) => it.__isSubRecipe && normalizeId(it.subRecipeId) === srId
      );
      if (already) return;

      const parentKey = `sub:${srId}`;
      // meta row (UI only)
      nextSelected.push({
        uiKey: parentKey,
        inventoryId: parentKey,
        inventoryName: m.menuName,
        quantity: 1,
        __isSubRecipe: true,
        subRecipeId: srId,
        subRecipeName: m.menuName,
      });
      nextQty[parentKey] = 1;

      // expand into real ingredients
      const expanded = expandSubRecipeIngredients(m, parentKey, 1);
      expanded.forEach((row) => {
        nextSelected.push(row);
        nextQty[qtyKeyOf(row)] = row.baseAmountFromSub;
      });
    });

    setSelectedIngredients(nextSelected);
    setIngredientQuantities(nextQty);
  };

  const handleSubRecipeQuantityChange = (srMetaRow, newValue) => {
    const parentKey = qtyKeyOf(srMetaRow);
    const val = newValue === "" ? "" : Math.max(0, Number(newValue));
    setIngredientQuantities((prev) => {
      const copy = { ...prev, [parentKey]: val };
      selectedIngredients.forEach((it) => {
        if (!it.__isSubRecipe && it.parentUIKey === parentKey) {
          const base = Number(it.baseAmountFromSub || 0);
          copy[qtyKeyOf(it)] = val === "" ? "" : base * (val || 0);
        }
      });
      return copy;
    });
  };

  const perUnitPrice = (ing) => {
    const inst = Array.isArray(ing.instances) ? ing.instances[0] : undefined;
    if (inst && Number(inst.purchaseAmount)) {
      return Number(inst.inventoryPrice) / Number(inst.purchaseAmount);
    }
    return Number(ing.pricePerUnit ?? 0);
  };

  const calculateTotalIngredientPrice = () =>
    selectedIngredients.reduce((total, ing) => {
      if (ing.__isSubRecipe) return total;
      const qKey = qtyKeyOf(ing);
      const quantity =
        Number(ingredientQuantities[qKey] ?? ing.quantity ?? 1) || 1;
      return total + (perUnitPrice(ing) * quantity || 0);
    }, 0);

  const subRecipeItems = useMemo(
    () => selectedIngredients.filter((i) => i.__isSubRecipe),
    [selectedIngredients]
  );
  const regularIngredients = useMemo(
    () => selectedIngredients.filter((i) => !i.__isSubRecipe),
    [selectedIngredients]
  );

  useEffect(() => {
    if (subRecipeItems.length) setShowSubRecipes(true);
  }, [subRecipeItems.length]);

  useEffect(() => {
    if (!open) {
      safeRevoke(image);
      safeRevoke(cropSrc);
      setImage(null);
      setCropSrc(null);
    }
    return () => {
      safeRevoke(image);
      safeRevoke(cropSrc);
    };
  }, [open, image, cropSrc]);

  useEffect(() => {
    const totalPrice = calculateTotalIngredientPrice();
    setPrice(totalPrice);
  }, [selectedIngredients, ingredientQuantities]);

  useEffect(() => {
    const isDuplicate = (menuItems || []).some(
      (menu) =>
        (menu.menuName || "").toLowerCase().trim() ===
        (name || "").toLowerCase().trim()
    );
    setNameError(
      isDuplicate ? "A menu item with this name already exists." : ""
    );
  }, [name, menuItems]);

  /* --------- Build payload: exclude meta rows, validate IDs ---------- */
  const buildIngredientsForSave = () => {
    const map = new Map();

    regularIngredients.forEach((row) => {
      // normalize/validate every time
      let invId =
        normalizeId(row.inventoryId) ||
        normalizeId(row._id) ||
        normalizeId(row.ingredientId);
      if (!isValidHexId(invId)) {
        console.warn("Dropping row with invalid inventoryId:", row.inventoryId);
        return; // skip invalid rows to avoid backend cast errors
      }

      const qKey = qtyKeyOf(row);
      const qty = Number(ingredientQuantities[qKey] ?? row.quantity ?? 1) || 1;
      const subId = row.fromSubRecipeId ? normalizeId(row.fromSubRecipeId) : "";
      const key = `${invId}::${subId || "none"}`;

      const basePayload = {
        inventoryId: invId,
        inventoryName: row.inventoryName,
        quantity: qty,
        ...(subId
          ? { subRecipeId: subId, subRecipeName: row.fromSubRecipeName }
          : {}),
      };

      const existing = map.get(key);
      if (!existing) map.set(key, basePayload);
      else existing.quantity += qty;
    });

    return Array.from(map.values());
  };

  const isFormValid = useMemo(() => {
    if (!name || nameError || !category) return false;
    const built = buildIngredientsForSave();
    return built.length > 0 && (price || price === 0);
  }, [
    name,
    nameError,
    category,
    selectedIngredients,
    ingredientQuantities,
    price,
  ]);

  /* ------------------------------- SAVE ------------------------------ */
  const handleSave = async () => {
    if (!imageFile) {
      alert("Please upload an image.");
      return;
    }

    const builtIngredients = buildIngredientsForSave();
    if (!builtIngredients.length) {
      alert("Please add at least one valid ingredient.");
      return;
    }

    const basePriceNum = Number(price) || calculateTotalIngredientPrice();

    const newItem = {
      name,
      translations,
      code: code || name,
      uniqueName,
      category: { id: category.categoryId, name: category.categoryName },
      menuImage: imageFile,
      // only send flattened ingredients (server derives menuIngredients)
      ingredients: builtIngredients,
      price: basePriceNum,
      menuPax,
      description,
      addOns: addons,
      status: "OptionsNotAdded",
      tags: tags.split(",").map((t) => t.trim()),
      processTime: parseInt(processTime, 10),
    };

    onSave(newItem);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setName("");
    setCategory("");
    setImage(null);
    setSelectedIngredients([]);
    setPrice("");
    setDescription("");
    setAddons([]);
    setIngredientQuantities({});
    safeRevoke(image);
    safeRevoke(cropSrc);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth>
        <DialogTitle>Create New Menu Item</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!nameError}
            helperText={nameError}
          />

          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 1, mb: 1 }}
            onClick={() => setShowTranslations(!showTranslations)}
          >
            {showTranslations ? "Hide Translations" : "Show Translations"}
          </Button>

          {showTranslations && (
            <>
              <Typography
                sx={{
                  mb: 1,
                  fontSize: "0.75rem",
                  lineHeight: 1.25,
                  color: "text.secondary",
                }}
              >
                Auto Translate uses Google Translate. Use cautiously since it
                can produce inaccurate translations
              </Typography>
              <Grid container spacing={2}>
                {["en", "tl", "zh", "th"].map((lg) => (
                  <Grid
                    key={lg}
                    item
                    xs={12}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <TextField
                      label={
                        {
                          en: "English",
                          tl: "Tagalog",
                          zh: "Chinese",
                          th: "Thai",
                        }[lg]
                      }
                      fullWidth
                      margin="normal"
                      value={translations[lg]}
                      onChange={(e) =>
                        setTranslations((prev) => ({
                          ...prev,
                          [lg]: e.target.value,
                        }))
                      }
                      InputLabelProps={{ shrink: Boolean(translations[lg]) }}
                    />
                    <Tooltip
                      title={`Translate to ${
                        {
                          en: "English",
                          tl: "Tagalog",
                          zh: "Chinese",
                          th: "Thai",
                        }[lg]
                      }`}
                    >
                      <span>
                        <IconButton
                          onClick={() => handleTranslate(lg)}
                          disabled={!name || loadingTranslation[lg]}
                        >
                          {loadingTranslation[lg] ? (
                            <CircularProgress size={24} />
                          ) : (
                            <GTranslateIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          <TextField
            label="Code (Optional)"
            fullWidth
            margin="normal"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            helperText="Leave empty to use the name as the code."
          />
          <TextField
            label="Unique Name (Optional)"
            fullWidth
            margin="normal"
            value={uniqueName}
            onChange={(e) => setUniqueName(e.target.value)}
          />

          <Autocomplete
            options={[
              ...menuCategories,
              { categoryId: "add-new", categoryName: "Add New Category" },
            ]}
            getOptionLabel={(option) => option.categoryName || ""}
            value={category}
            onChange={(event, newValue) => {
              if (newValue && newValue.categoryId === "add-new") {
                setIsAddingCategory(true);
              } else {
                setCategory(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                variant="outlined"
                margin="normal"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.categoryId}>
                <span style={{ flexGrow: 1 }}>{option.categoryName}</span>
                {option.categoryId !== "add-new" && (
                  <Tooltip title="Delete category">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(option.categoryId);
                      }}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </li>
            )}
          />

          {isAddingCategory && (
            <AddMenuCategoryDialog
              open={isAddingCategory}
              onClose={() => setIsAddingCategory(false)}
              onCategoryAdded={(newCategory) => {
                setMenuCategories((prev) => [...prev, newCategory]);
                setCategory(newCategory);
                setIsAddingCategory(false);
              }}
            />
          )}

          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Button
                variant="contained"
                component="label"
                startIcon={<PhotoCamera />}
              >
                Upload Image
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleImageUpload}
                />
              </Button>
            </Grid>
            {image && (
              <Grid item>
                <img
                  src={image}
                  alt="Preview"
                  style={{ width: 100, height: 100, objectFit: "cover" }}
                />
              </Grid>
            )}
          </Grid>
          {imageError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {imageError}
            </Typography>
          )}

          <TextField
            label="Preparation Time (in minutes)"
            fullWidth
            margin="normal"
            type="number"
            value={processTime}
            onChange={(e) => setProcessTime(e.target.value)}
            helperText="Enter the estimated preparation time for this menu item"
          />

          {/* INGREDIENTS */}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Ingredients
          </Typography>

          <Autocomplete
            options={ingredients}
            getOptionLabel={(option) =>
              `${option.inventoryName} - ${option.unitOfMeasurement || ""}`
            }
            onChange={(event, newValue) => {
              if (newValue) handleAddIngredient(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add New Ingredients"
                margin="normal"
                variant="outlined"
              />
            )}
          />

          <List>
            {regularIngredients.map((ingredient) => {
              const qKey = qtyKeyOf(ingredient);
              return (
                <ListItem key={qKey}>
                  <ListItemText
                    primary={`${ingredient.inventoryName} (${
                      ingredient.unitOfMeasurement || "UOM"
                    })`}
                  />
                  <TextField
                    label="Quantity"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={
                      ingredientQuantities[qKey] ?? ingredient.quantity ?? ""
                    }
                    onChange={(e) => {
                      const v =
                        e.target.value === ""
                          ? ""
                          : Math.max(0, Number(e.target.value));
                      handleQuantityChange(qKey, v);
                    }}
                    size="small"
                    sx={{ width: 80, mr: 1 }}
                  />
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveIngredient(ingredient)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              );
            })}
          </List>

          {/* SUB-RECIPES */}
          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 2, mb: 1 }}
            onClick={() => setShowSubRecipes((s) => !s)}
          >
            {showSubRecipes ? "Hide Sub-Recipes" : "Show Sub-Recipes"}
          </Button>

          <Collapse in={showSubRecipes} timeout="auto" unmountOnExit>
            <List dense sx={{ mt: 1 }}>
              {subRecipeItems.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No sub-recipes added." />
                </ListItem>
              ) : (
                subRecipeItems.map((sr) => {
                  const qKey = qtyKeyOf(sr);
                  return (
                    <ListItem key={qKey}>
                      <ListItemText
                        primary={sr.subRecipeName || sr.inventoryName}
                        secondary="Sub-Recipe"
                      />
                      <TextField
                        label="Quantity"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={ingredientQuantities[qKey] ?? sr.quantity ?? ""}
                        onChange={(e) => {
                          const v =
                            e.target.value === ""
                              ? ""
                              : Math.max(0, Number(e.target.value));
                          handleSubRecipeQuantityChange(sr, v);
                        }}
                        size="small"
                        sx={{ width: 80, mr: 1 }}
                      />
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveIngredient(sr)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  );
                })
              )}
              <ListItem>
                <Button
                  variant="outlined"
                  onClick={() => setSubRecipeOpen(true)}
                >
                  Add Sub-Recipe
                </Button>
              </ListItem>
            </List>
          </Collapse>

          <TextField
            label="Base Price"
            fullWidth
            margin="normal"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            helperText={`Total Price of ingredients: ${calculateTotalIngredientPrice()}`}
          />

          <TextField
            label="No. of Pax"
            fullWidth
            margin="normal"
            type="number"
            value={menuPax}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setMenuPax(value < 1 ? 1 : value);
            }}
            helperText="Number of people this item is meant to serve (minimum 1)"
          />

          <TextField
            label="Description (Optional)"
            fullWidth
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <List>
            {addons.map((addonId) => {
              const addon = (menuItems || []).find(
                (menuItem) => normalizeId(menuItem._id) === normalizeId(addonId)
              );
              return (
                <ListItem key={normalizeId(addonId)}>
                  <ListItemText primary={addon?.menuName || "Unknown Addon"} />
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setAddons(
                        addons.filter(
                          (id) => normalizeId(id) !== normalizeId(addonId)
                        )
                      )
                    }
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              );
            })}
          </List>

          <TextField
            label="Tags"
            fullWidth
            margin="normal"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            helperText="If more than one tag, separate with commas"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" disabled={!isFormValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <HandleCropMenuImage
        open={cropperOpen}
        imageSrc={cropSrc}
        onCancel={handleCropCancel}
        onSave={handleCropSave}
      />

      <SubRecipeDialog
        open={subRecipeOpen}
        onClose={() => setSubRecipeOpen(false)}
        menuItems={menuItems}
        excludeIds={[]}
        onAdd={handleAddSubRecipeItems}
        allowMultiple={true}
      />
    </>
  );
};

export default CreateMenuItemDialog;
